import type { ComponentType } from "react";
import { createBrowserRouter } from "react-router";

import { PATHS } from "@/routes/paths";
import { ProtectedRoute } from "@/routes/ProtectedRoute";
import { PermissionGuard } from "@/shared/components/PermissionGuard";
import { RouteErrorBoundary } from "@/shared/components/RouteErrorBoundary";
import { AppLayout } from "@/shared/layout/AppLayout";

/**
 * Helper para rotas que exigem permissão de módulo.
 * Carrega o componente sob demanda e envolve com `<PermissionGuard>`.
 *
 * Aceita uma única permissão (modulo + ação) **ou** uma lista de permissões
 * alternativas via array (`[{ modulo, acao }, ...]`) — o acesso é liberado
 * se o usuário tiver qualquer uma delas.
 */
function lazyWithGuard(
  loader: () => Promise<Record<string, unknown>>,
  componentName: string,
  moduloOuPermissoes: string | ReadonlyArray<{ modulo: string; acao?: string }>,
  acao?: string
) {
  return async () => {
    const mod = await loader();
    const Component = mod[componentName] as ComponentType;
    const Guarded = () =>
      Array.isArray(moduloOuPermissoes) ? (
        <PermissionGuard permissoes={moduloOuPermissoes}>
          <Component />
        </PermissionGuard>
      ) : (
        <PermissionGuard modulo={moduloOuPermissoes as string} acao={acao}>
          <Component />
        </PermissionGuard>
      );
    return { Component: Guarded };
  };
}

export const router = createBrowserRouter([
  {
    path: "/login",
    lazy: async () => {
      const { LoginPage } = await import("@/features/auth/pages/LoginPage");
      return { Component: LoginPage };
    },
  },
  {
    path: "/primeiro-acesso",
    lazy: async () => {
      const { PrimeiroAcessoPage } =
        await import("@/features/auth/pages/PrimeiroAcessoPage");
      return { Component: PrimeiroAcessoPage };
    },
  },
  {
    path: "/redefinir-senha",
    lazy: async () => {
      const { RedefinirSenhaLinkPage } =
        await import("@/features/auth/pages/RedefinirSenhaLinkPage");
      return { Component: RedefinirSenhaLinkPage };
    },
  },
  {
    path: "/confirmar-email",
    lazy: async () => {
      const { ConfirmarEmailPage } =
        await import("@/features/auth/pages/ConfirmarEmailPage");
      return { Component: ConfirmarEmailPage };
    },
  },
  {
    path: `${PATHS.confirmarRecebimento}/:token`,
    lazy: async () => {
      const { ConfirmarRecebimentoPage } =
        await import("@/features/instalacao/inspecao-qualidade/pages/ConfirmarRecebimentoPage");
      return { Component: ConfirmarRecebimentoPage };
    },
  },
  {
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: null },
      {
        path: PATHS.dashboard,
        lazy: async () => {
          const { DashboardPage } =
            await import("@/features/dashboard/pages/DashboardPage");
          return { Component: DashboardPage };
        },
      },
      {
        path: PATHS.meuPerfil,
        lazy: async () => {
          const { MeuPerfilPage } =
            await import("@/features/meu-perfil/pages/MeuPerfilPage");
          return { Component: MeuPerfilPage };
        },
      },
      {
        path: PATHS.precosPorLoja,
        lazy: async () => {
          const { PrecosPorLojaPage } =
            await import("@/features/comissoes/precos-por-loja/pages/PrecosPorLojaPage");
          return { Component: PrecosPorLojaPage };
        },
      },
      {
        path: PATHS.comissionados,
        lazy: async () => {
          const { ComissionadosPage } =
            await import("@/features/comissoes/comissionados/pages/ComissionadosPage");
          return { Component: ComissionadosPage };
        },
      },
      {
        path: PATHS.regrasComissao,
        lazy: async () => {
          const { RegrasComissaoPage } =
            await import("@/features/comissoes/regras-comissao/pages/RegrasComissaoPage");
          return { Component: RegrasComissaoPage };
        },
      },
      {
        path: PATHS.campanhasIncentivo,
        lazy: async () => {
          const { CampanhasIncentivoPage } =
            await import("@/features/comissoes/campanhas-incentivo/pages/CampanhasIncentivoPage");
          return { Component: CampanhasIncentivoPage };
        },
      },
      {
        path: PATHS.valoresInstalacao,
        lazy: async () => {
          const { ValoresInstalacaoPage } =
            await import("@/features/comissoes/valores-instalacao/pages/ValoresInstalacaoPage");
          return { Component: ValoresInstalacaoPage };
        },
      },
      {
        path: PATHS.pagamentoComissao,
        lazy: async () => {
          const { PagamentosPage } =
            await import("@/features/comissoes/pagamento-comissoes/pages/PagamentosPage");
          return { Component: PagamentosPage };
        },
      },
      {
        path: PATHS.aprovacaoComissao,
        lazy: async () => {
          const { AprovacaoComissoesPage } =
            await import("@/features/comissoes/aprovacao-comissoes/pages/AprovacaoComissoesPage");
          return { Component: AprovacaoComissoesPage };
        },
      },
      {
        path: PATHS.fornecedores,
        lazy: lazyWithGuard(
          () =>
            import("@/features/compras/fornecedores/pages/FornecedoresPage"),
          "FornecedoresPage",
          "Compras - Fornecedores",
          "CONS"
        ),
      },
      {
        path: PATHS.pedidosCompra,
        lazy: lazyWithGuard(
          () =>
            import("@/features/compras/pedidos-compra/pages/PedidosCompraPage"),
          "PedidosCompraPage",
          "Compras - Pedidos de Compra",
          "CONS"
        ),
      },
      {
        path: PATHS.ocorrencias,
        lazy: lazyWithGuard(
          () => import("@/features/ocorrencias/pages/OcorrenciasPage"),
          "OcorrenciasPage",
          "Gestão de Ocorrências",
          "CONS"
        ),
      },
      {
        path: PATHS.concessionarias,
        lazy: lazyWithGuard(
          () => import("@/features/concessionarias/pages/ConcessionariasPage"),
          "ConcessionariasPage",
          "Concessionárias - Concessionárias",
          "CONS"
        ),
      },
      {
        path: PATHS.empresas,
        lazy: lazyWithGuard(
          () => import("@/features/empresas/pages/EmpresasPage"),
          "EmpresasPage",
          // Módulo do back (PermissaoModulos.Empresas) — sem prefixo, ao
          // contrário dos vizinhos que usam o padrão "Grupo - Item".
          "Empresas",
          "CONS"
        ),
      },
      {
        path: PATHS.recebimento,
        lazy: lazyWithGuard(
          () => import("@/features/compras/recebimento/pages/RecebimentosPage"),
          "RecebimentosPage",
          "Compras - Recebimentos",
          "CONS"
        ),
      },
      {
        path: PATHS.usuarios,
        lazy: lazyWithGuard(
          () => import("@/features/usuarios/usuarios/pages/UsuariosPage"),
          "UsuariosPage",
          "Usuários",
          "CONS"
        ),
      },
      {
        path: PATHS.importarUsuarios,
        lazy: lazyWithGuard(
          () =>
            import("@/features/usuarios/usuarios/pages/ImportarUsuariosPage"),
          "ImportarUsuariosPage",
          "Usuários",
          "CONS"
        ),
      },
      {
        path: PATHS.perfis,
        lazy: lazyWithGuard(
          () => import("@/features/usuarios/perfis/pages/PerfisPage"),
          "PerfisPage",
          "Permissões",
          "CONS"
        ),
      },
      {
        path: PATHS.whitelistDominios,
        lazy: lazyWithGuard(
          () =>
            import("@/features/configuracoes/whitelist-dominios/pages/WhitelistDominiosPage"),
          "WhitelistDominiosPage",
          "Configurações",
          "CONS"
        ),
      },
      {
        path: PATHS.politicaSenhas,
        lazy: lazyWithGuard(
          () =>
            import("@/features/configuracoes/politica-senhas/pages/PoliticaSenhasPage"),
          "PoliticaSenhasPage",
          "Configurações",
          "CONS"
        ),
      },
      {
        path: PATHS.configuracaoSessao,
        lazy: lazyWithGuard(
          () =>
            import("@/features/configuracoes/configuracao-sessao/pages/ConfiguracoesSessaoPage"),
          "ConfiguracoesSessaoPage",
          "Configurações",
          "CONS"
        ),
      },
      {
        path: PATHS.marcas,
        lazy: lazyWithGuard(
          () => import("@/features/configuracoes/marcas/pages/MarcasPage"),
          "MarcasPage",
          "Configurações",
          "CONS"
        ),
      },
      {
        path: PATHS.categoriasProduto,
        lazy: lazyWithGuard(
          () =>
            import("@/features/configuracoes/categoria-produto/pages/CategoriasProdutoPage"),
          "CategoriasProdutoPage",
          "Configurações",
          "CONS"
        ),
      },
      {
        path: PATHS.categoriasFornecedor,
        lazy: lazyWithGuard(
          () =>
            import("@/features/configuracoes/categoria-fornecedor/pages/CategoriasFornecedorPage"),
          "CategoriasFornecedorPage",
          "Configurações",
          "CONS"
        ),
      },
      {
        path: PATHS.regrasCompras,
        lazy: lazyWithGuard(
          () =>
            import("@/features/configuracoes/regras-compras/pages/RegrasComprasPage"),
          "RegrasComprasPage",
          "Configurações",
          "CONS"
        ),
      },
      {
        path: PATHS.produtos,
        lazy: lazyWithGuard(
          () => import("@/features/estoque/produtos/pages/ProdutosPage"),
          "ProdutosPage",
          "Estoque - Produtos",
          "CONS"
        ),
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: PATHS.inventarios,
        lazy: lazyWithGuard(
          () =>
            import("@/features/estoque/inventario/pages/InventariosConsultaPage"),
          "InventariosConsultaPage",
          "Estoque - Ajuste/Inventário",
          "CONS"
        ),
      },
      {
        path: PATHS.movimentacoes,
        // Tela acessível por qualquer perfil que precise listar OU registrar
        // movimentações. Sem isso, quem tem só "Ajuste/Inventário - CRIAR" não
        // chegava ao dropdown "Nova Movimentação > Ajuste / Devolução / Consumo".
        // A listagem dentro da tela continua escondida sem `VER` em Movimentações.
        lazy: lazyWithGuard(
          () =>
            import("@/features/estoque/movimentacoes/pages/MovimentacoesPage"),
          "MovimentacoesPage",
          [
            { modulo: "Estoque - Movimentações", acao: "VER" },
            { modulo: "Estoque - Movimentações", acao: "CRIAR" },
            { modulo: "Estoque - Ajuste/Inventário", acao: "CRIAR" },
          ]
        ),
      },
      {
        path: PATHS.posicaoEstoque,
        lazy: lazyWithGuard(
          () =>
            import("@/features/estoque/posicao-estoque/pages/PosicaoEstoquePage"),
          "PosicaoEstoquePage",
          "Estoque - Posição",
          "CONS"
        ),
      },
      {
        path: PATHS.recebimentosTransferencia,
        lazy: lazyWithGuard(
          () =>
            import("@/features/estoque/recebimentos-transferencia/pages/RecebimentosTransferenciaPage"),
          "RecebimentosTransferenciaPage",
          "Estoque - Recebimento de Transferência",
          "CONS"
        ),
      },
      {
        path: PATHS.rastreamentoEtiquetas,
        lazy: lazyWithGuard(
          () =>
            import("@/features/estoque/rastreamento-etiquetas/pages/RastreamentoEtiquetasPage"),
          "RastreamentoEtiquetasPage",
          "Estoque - Rastreamento",
          "CONS"
        ),
      },
      {
        path: PATHS.simulacoesVenda,
        lazy: lazyWithGuard(
          () =>
            import("@/features/gestao-vendas/simulacoes/pages/SimulacoesVendaPage"),
          "SimulacoesVendaPage",
          "Gestão de Vendas - Simulação",
          "CONS"
        ),
      },
      {
        path: PATHS.pedidosVenda,
        lazy: lazyWithGuard(
          () =>
            import("@/features/gestao-vendas/pedidos-venda/pages/PedidosVendaPage"),
          "PedidosVendaPage",
          "Gestão de Vendas - Pedidos de Venda",
          "CONS"
        ),
      },
      {
        path: PATHS.vendas,
        lazy: lazyWithGuard(
          () =>
            import("@/features/gestao-vendas/vendas/pages/VendasConsultaPage"),
          "VendasConsultaPage",
          "Gestão de Vendas - Vendas",
          "CONS"
        ),
      },
      {
        path: PATHS.dashboardVendas,
        lazy: lazyWithGuard(
          () =>
            import("@/features/gestao-vendas/vendas/pages/DashboardVendasPage"),
          "DashboardVendasPage",
          "Gestão de Vendas - Vendas",
          "CONS"
        ),
      },
      {
        path: PATHS.dashboardFiscal,
        lazy: lazyWithGuard(
          () =>
            import("@/features/fiscal/dashboard-fiscal/pages/DashboardFiscalPage"),
          "DashboardFiscalPage",
          "Fiscal – Dashboard",
          "CONS"
        ),
      },
      {
        path: PATHS.regrasTributacao,
        lazy: lazyWithGuard(
          () =>
            import("@/features/fiscal/regras-tributacao/pages/RegrasTributacaoPage"),
          "RegrasTributacaoPage",
          "Fiscal - Regras de Tributação",
          "CONS"
        ),
      },
      {
        path: PATHS.apuracaoIRPJeCSLL,
        lazy: lazyWithGuard(
          () =>
            import("@/features/fiscal/apuracao-irpj-csll/pages/ApuracaoIRPJCSLLPage"),
          "ApuracaoIRPJCSLLPage",
          "Fiscal - Apuração IRPJ/CSLL",
          "CONS"
        ),
      },
      {
        path: PATHS.relatorioFiscal,
        lazy: lazyWithGuard(
          () =>
            import("@/features/fiscal/relatorio-fiscal/pages/RelatorioFiscalPage"),
          "RelatorioFiscalPage",
          "Fiscal – Relatório",
          "CONS"
        ),
      },
      {
        path: PATHS.agenda,
        lazy: lazyWithGuard(
          () => import("@/features/instalacao/agenda/pages/AgendaPage"),
          "AgendaPage",
          "Instalação – Agenda",
          "CONS"
        ),
      },
      {
        path: PATHS.minhasOs,
        lazy: lazyWithGuard(
          () => import("@/features/instalacao/minhas-os/pages/MinhasOSPage"),
          "MinhasOSPage",
          "Instalação – Execução",
          "CONS"
        ),
      },
      {
        path: PATHS.inspecaoQualidade,
        lazy: lazyWithGuard(
          () =>
            import("@/features/instalacao/inspecao-qualidade/pages/InspecaoQualidadePage"),
          "InspecaoQualidadePage",
          "Qualidade – Inspeção",
          "CONS"
        ),
      },
      {
        path: PATHS.indicadoresQualidade,
        lazy: lazyWithGuard(
          () =>
            import("@/features/instalacao/indicador-qualidade/pages/IndicadoresQualidadePage"),
          "IndicadoresQualidadePage",
          "Qualidade – Indicadores",
          "CONS"
        ),
      },
      {
        path: PATHS.garantiaTerceiro,
        lazy: lazyWithGuard(
          () =>
            import("@/features/instalacao/garantia-terceiro/pages/NovaGarantiaTerceiroPage"),
          "NovaGarantiaTerceiroPage",
          "Instalação – Garantia",
          "CRIAR"
        ),
      },
      {
        path: PATHS.auditoria,
        lazy: lazyWithGuard(
          () => import("@/features/auditoria/pages/LogAuditoriaPage"),
          "LogAuditoriaPage",
          "Auditoria - Log de Auditoria",
          "CONS"
        ),
      },
      {
        path: "*",
        lazy: async () => {
          const { NotFoundPage } = await import("@/shared/pages/NotFoundPage");
          return { Component: NotFoundPage };
        },
      },
    ],
  },
]);
